import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function HR() {
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", role: "", department: "Operations",
    location: "HQ", skills: "",
  });

  const load = () => api.get("/employees").then((r) => setEmps(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Employee added");
      setOpen(false);
      setForm({ full_name: "", email: "", role: "", department: "Operations", location: "HQ", skills: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    await api.delete(`/employees/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="hr-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">/ HUMAN RESOURCES</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Team &amp; Training</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Digital employee files, skills matrix, onboarding.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-employee-btn" className="rounded-sm bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm" data-testid="add-employee-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">New Employee</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Full name</Label>
                <Input required data-testid="emp-name" className="rounded-sm mt-1.5"
                  value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required data-testid="emp-email" className="rounded-sm mt-1.5"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Input required data-testid="emp-role" className="rounded-sm mt-1.5"
                    value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
                </div>
                <div>
                  <Label>Department</Label>
                  <Input required data-testid="emp-department" className="rounded-sm mt-1.5"
                    value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Location</Label>
                  <Input data-testid="emp-location" className="rounded-sm mt-1.5"
                    value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <Label>Skills (comma-sep)</Label>
                  <Input data-testid="emp-skills" className="rounded-sm mt-1.5"
                    value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="emp-submit" className="rounded-sm bg-primary hover:bg-primary/90">
                  Add
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Headcount", value: emps.length },
          { label: "Departments", value: new Set(emps.map((e) => e.department)).size },
          { label: "Locations", value: new Set(emps.map((e) => e.location)).size },
          { label: "Total Skills", value: emps.reduce((a, e) => a + (e.skills?.length || 0), 0) },
        ].map((k) => (
          <div key={k.label} className="border border-border bg-card p-6" data-testid={`hr-kpi-${k.label.toLowerCase()}`}>
            <div className="overline text-muted-foreground">{k.label}</div>
            <div className="mt-3 font-display text-4xl font-bold">{k.value}</div>
          </div>
        ))}
      </div>

      {emps.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center" data-testid="hr-empty">
          <Users className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-4 font-display text-lg">No employees yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your first team member to start.</p>
        </div>
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 overline text-muted-foreground">Name</th>
                <th className="px-4 py-3 overline text-muted-foreground">Role</th>
                <th className="px-4 py-3 overline text-muted-foreground">Department</th>
                <th className="px-4 py-3 overline text-muted-foreground">Location</th>
                <th className="px-4 py-3 overline text-muted-foreground">Skills</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {emps.map((e) => (
                <tr key={e.id} className="border-b border-border hover:bg-secondary/50" data-testid={`emp-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{e.full_name}</div>
                    <div className="text-xs text-muted-foreground">{e.email}</div>
                  </td>
                  <td className="px-4 py-3">{e.role}</td>
                  <td className="px-4 py-3">{e.department}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.location}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(e.skills || []).map((s) => (
                        <span key={s} className="text-[10px] font-mono-plex uppercase tracking-widest px-2 py-0.5 border border-border">
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => del(e.id)} data-testid={`emp-delete-${e.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
